import { createContext, useContext } from "react";

/**
 * The scope pricing edits apply to: the global sheet, or one specific store.
 *
 * `storeId` of `null` means global. A store is identified by id alone; its name is held
 * for display only, so nothing downstream depends on it.
 */
export interface PricingScope {
    storeId: number | null;
    storeName: string | null;
}

export const PricingScopeContext = createContext<PricingScope | undefined>(
    undefined
);

/**
 * Read the current pricing scope. THROWS when used outside the provider.
 *
 * The throw is the point of this module. On the DELIVER line the equivalent scope is
 * threaded as optional props into ten pricing forms, and the API helpers drop an
 * undefined scope from the request body — so the backend widens the write and the
 * omission type-checks cleanly. That mechanism caused a real staging regression: a
 * rebuilt PricingSettings dropped every storeId reference and TypeScript never
 * complained (bead pollytech-llc-v8q; fixed in commit d667971).
 *
 * Mandatory-by-default means a pricing component that forgets scope fails loudly at
 * render instead of silently writing global prices from a screen showing a store name.
 */
export const usePricingScope = (): PricingScope => {
    const scope = useContext(PricingScopeContext);

    if (scope === undefined) {
        throw new Error(
            "usePricingScope must be used inside a PricingScopeProvider. " +
                "A pricing component without an explicit scope would write global " +
                "prices, affecting every store."
        );
    }

    return scope;
};
